import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, NavLink, useLocation } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight,
  CheckCircle,
  EnvelopeSimple,
  HouseLine,
  Lightning,
  MapPin,
  Phone,
  ShieldCheck,
  SquaresFour,
  SunDim,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Toaster, toast } from "@/components/ui/sonner";
import { CAPABILITY_CARD_ICON_SIZE_PX, TRUST_CHECK_ICON_SIZE_PX } from "@/constants/appConfig";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

const initializeGoogleAnalytics = () => {
  if (!GA_MEASUREMENT_ID || document.getElementById("google-analytics-script")) {
    return;
  }

  const script = document.createElement("script");
  script.id = "google-analytics-script";
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("config", GA_MEASUREMENT_ID, { send_page_view: false });
};

const trackPageView = (path) => {
  if (!GA_MEASUREMENT_ID || typeof window.gtag !== "function") {
    return;
  }

  window.gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
};

const initialQuoteForm = {
  name: "",
  company_role: "",
  email: "",
  phone: "",
  project_type: "Custom skylight",
  location: "",
  timeline: "",
  details: "",
};

const imageUrls = {
  hero:
    "https://customer-assets.emergentagent.com/job_bright-installations/artifacts/vplforxx_888%20Douglas%232.JPEG",
  detail:
    "https://customer-assets.emergentagent.com/job_bright-installations/artifacts/vrtidtbl_RIDGE%20INSTALLATION.JPEG",
  architects:
    "https://customer-assets.emergentagent.com/job_bright-installations/artifacts/7iy84axb_IMG_20211124_084742_2.JPEG",
  geometric:
    "https://customer-assets.emergentagent.com/job_bright-installations/artifacts/ad415sj2_IMG_4226%20%281%29.JPEG",
  dome:
    "https://customer-assets.emergentagent.com/job_bright-installations/artifacts/qtvzx8c1_IMG_9880%20%281%29.JPEG",
  pyramid:
    "https://customer-assets.emergentagent.com/job_bright-installations/artifacts/acr9xluh_IMG_4226%20%281%29.JPEG",
  polygon:
    "https://customer-assets.emergentagent.com/job_bright-installations/artifacts/t1e025sl_Hearst%20Castle.JPEG",
  commercial:
    "https://customer-assets.emergentagent.com/job_bright-installations/artifacts/n55dazo0_20221207_090715.JPEG",
  additionalOffice:
    "https://customer-assets.emergentagent.com/job_bright-installations/artifacts/cjnny5t9_IMG_2713.PNG",
  additionalMalibu:
    "https://customer-assets.emergentagent.com/job_bright-installations/artifacts/w50xbfob_MALIBU%20INTERIOR%20%281%29.JPEG",
  curvedExterior:
    "https://customer-assets-v7afamib.emergentagent.net/job_bright-installations/artifacts/7x8d3wd4_C7D49768-7690-4AC2-9757-124F2BF00572.PNG",
  blueprintHipRidge:
    "https://customer-assets-v7afamib.emergentagent.net/job_bright-installations/artifacts/c748zenx_WEBSITE%20DRAWING%20HIP%20RIDGE.PNG",
  blueprintSpa:
    "https://customer-assets-v7afamib.emergentagent.net/job_bright-installations/artifacts/sg3uuozv_WEBSITE%20DRAWING%20SPA.PNG",
  blueprintLeanTo:
    "https://customer-assets-v7afamib.emergentagent.net/job_bright-installations/artifacts/8uqzitew_WEBSITE%20DRAWING-LEAN%20TO.PNG",
  blueprintRadius:
    "https://customer-assets-v7afamib.emergentagent.net/job_bright-installations/artifacts/rlitwcg0_WEBSITE%20DRAWING-RADIUS%20SKYLIGHT.PNG",
  blueprintHistoricalRetail:
    "https://customer-assets-v7afamib.emergentagent.net/job_bright-installations/artifacts/4mjq93g5_image0%20%283%29.png",
};

const navItems = [
  { label: "Work", to: "/portfolio" },
  { label: "Systems", to: "/designs" },
  { label: "Process", to: "/process" },
  { label: "Quote", to: "/contact" },
];

const capabilities = [
  {
    title: "Custom Retractable",
    text: "Operable skylight systems designed for distinctive luxury residences and specialty spaces.",
    icon: Lightning,
  },
  {
    title: "Hip & Gable Ridge",
    text: "Clean structural lines for pitched roofs, great rooms, pool enclosures, and feature spaces.",
    icon: HouseLine,
  },
  {
    title: "Pyramid & Polygon",
    text: "Architectural focal point built around natural daylight, glazing performance, and finish quality.",
    icon: SquaresFour,
  },
  {
    title: "Segmented Domes",
    text: "Custom daylight systems for commercial, residential, and custom design projects.",
    icon: SunDim,
  },
];

const portfolio = [
  {
    title: "Residential Ridge Skylights",
    category: "Custom Home",
    image: imageUrls.additionalOffice,
    blueprint: imageUrls.blueprintHipRidge,
    text: "Bright, symmetrical skylights integrated into a finished residential ceiling with clean interior detailing.",
  },
  {
    title: "Commercial Atrium Skylight",
    category: "Commercial Retail",
    image: imageUrls.additionalMalibu,
    blueprint: imageUrls.blueprintSpa,
    text: "A finished commercial daylight system that transforms a dining and retail environment with natural light.",
  },
  {
    title: "Historical Retail Feature",
    category: "Retail & Hospitality",
    image: imageUrls.polygon,
    blueprint: imageUrls.blueprintHistoricalRetail,
    text: "A high-impact glass roof feature that brings architectural character and daylight into a landmark retail space.",
  },
  {
    title: "Large-Scale Daylight Hall",
    category: "Adaptive Reuse",
    image: imageUrls.curvedExterior,
    blueprint: imageUrls.blueprintRadius,
    text: "Expansive skylight and glass work supporting a large open interior with dramatic, reliable daylight.",
  },
];

const additionalPhotos = [
  {
    id: "office-skylight",
    image: imageUrls.pyramid,
    alt: "Finished office skylight project",
  },
  {
    id: "malibu-interior",
    image: imageUrls.commercial,
    alt: "Finished Malibu interior skylight project",
    imageClassName: "focal-top",
  },
];

const projectTypes = [
  "Custom skylight",
  "Operable / retractable skylight",
  "Glass enclosure",
  "Commercial dome / ridge system",
  "Replacement or modernization",
];

const SectionIntro = ({ eyebrow, title, body, align = "left" }) => (
  <div className={`section-intro ${align === "center" ? "mx-auto text-center" : ""}`}>
    <p className="eyebrow" data-testid={`${eyebrow.toLowerCase().replaceAll(" ", "-")}-eyebrow`}>
      {eyebrow}
    </p>
    <h2 data-testid={`${eyebrow.toLowerCase().replaceAll(" ", "-")}-heading`}>{title}</h2>
    {body && <p data-testid={`${eyebrow.toLowerCase().replaceAll(" ", "-")}-body`}>{body}</p>}
  </div>
);

const Header = () => (
  <header className="site-header" data-testid="site-header">
    <NavLink to="/" className="brand" data-testid="header-brand-link">
      <span className="brand-mark" data-testid="header-brand-mark">SS</span>
      <span data-testid="header-brand-text">Sky Systems</span>
    </NavLink>
    <nav className="desktop-nav" data-testid="desktop-navigation">
      {navItems.map((item) => (
        <NavLink key={item.to} to={item.to} data-testid={`nav-link-${item.label.toLowerCase()}`}>
          {item.label}
        </NavLink>
      ))}
    </nav>
    <a className="header-phone" href="tel:9493621937" data-testid="header-phone-link">
      <Phone size={18} weight="duotone" />
      949-362-1937
    </a>
  </header>
);

const QuoteField = ({ id, label, value, onChange, type = "text", required = false, placeholder = "", testId }) => (
  <div className="field-group">
    <Label htmlFor={id} data-testid={`${testId}-label`}>{label}</Label>
    <Input
      id={id}
      type={type}
      required={required}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="sharp-input"
      placeholder={placeholder}
      data-testid={`${testId}-input`}
    />
  </div>
);

const QuoteTextArea = ({ value, onChange }) => (
  <div className="field-group">
    <Label htmlFor="details" data-testid="quote-details-label">Project Details</Label>
    <Textarea
      id="details"
      required
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="sharp-textarea"
      data-testid="quote-details-textarea"
    />
  </div>
);

const QuoteProjectTypeSelect = ({ value, onChange }) => (
  <div className="field-group">
    <Label htmlFor="project-type" data-testid="quote-project-type-label">Project Type</Label>
    <select
      id="project-type"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="sharp-select"
      data-testid="quote-project-type-select"
    >
      {projectTypes.map((type) => (
        <option value={type} key={type}>{type}</option>
      ))}
    </select>
  </div>
);

const useQuoteForm = () => {
  const [form, setForm] = useState(initialQuoteForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState("");

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const submitQuote = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setConfirmation("");

    try {
      const response = await axios.post(`${API}/quotes`, form);
      const message = response.data.email_sent
        ? "Your request was sent to Sky Systems. We’ll follow up directly."
        : "Your request was saved for Sky Systems. We’ll follow up directly.";
      setConfirmation(message);
      toast.success("Quote request received", { description: message });
      setForm(initialQuoteForm);
    } catch (error) {
      toast.error("Please check the form", {
        description: "We couldn’t submit the request. Make sure every required field is complete.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return { form, updateField, submitQuote, isSubmitting, confirmation };
};

const QuoteContactFields = ({ form, updateField }) => (
  <div className="form-grid">
    <QuoteField id="name" label="Name" value={form.name} onChange={(value) => updateField("name", value)} required testId="quote-name" />
    <QuoteField id="company" label="Company / Role" value={form.company_role} onChange={(value) => updateField("company_role", value)} required testId="quote-company" />
    <QuoteField id="email" label="Email" type="email" value={form.email} onChange={(value) => updateField("email", value)} required testId="quote-email" />
    <QuoteField id="phone" label="Phone" value={form.phone} onChange={(value) => updateField("phone", value)} required testId="quote-phone" />
  </div>
);

const QuoteProjectFields = ({ form, updateField }) => (
  <>
    <QuoteProjectTypeSelect value={form.project_type} onChange={(value) => updateField("project_type", value)} />
    <div className="form-grid">
      <QuoteField id="location" label="Project Location" value={form.location} onChange={(value) => updateField("location", value)} required testId="quote-location" />
      <QuoteField id="timeline" label="Timeline" value={form.timeline} onChange={(value) => updateField("timeline", value)} placeholder="Planning, bidding, active build..." testId="quote-timeline" />
    </div>
    <QuoteTextArea value={form.details} onChange={(value) => updateField("details", value)} />
  </>
);

const QuoteForm = () => {
  const { form, updateField, submitQuote, isSubmitting, confirmation } = useQuoteForm();

  return (
    <form className="quote-form" onSubmit={submitQuote} data-testid="quote-request-form">
      <QuoteContactFields form={form} updateField={updateField} />
      <QuoteProjectFields form={form} updateField={updateField} />
      <Button type="submit" className="primary-button w-full" disabled={isSubmitting} data-testid="quote-submit-button">
        {isSubmitting ? "Sending request..." : "Send Quote Request"}
        <ArrowRight size={18} weight="bold" />
      </Button>
      {confirmation && <p className="confirmation" data-testid="quote-confirmation-message">{confirmation}</p>}
    </form>
  );
};

const HeroSection = () => (
  <section className="hero-section" data-testid="hero-section">
    <div className="hero-media" aria-hidden="true">
      <img src={imageUrls.hero} alt="Finished architectural skylight in a bright premium interior" data-testid="hero-image" />
    </div>
    <div className="hero-overlay" />
    <div className="hero-content">
      <p className="eyebrow light" data-testid="hero-eyebrow">SINCE 1990</p>
      <h1 data-testid="hero-heading">Architectural Skylights, Professional Installation & Premium Glass Enclosures</h1>
      <p data-testid="hero-subheading">
        Partnering with general contractors, custom home builders, and architects across Southern California to deliver clean, reliable, finished daylight systems.
      </p>
      <div className="hero-actions" data-testid="hero-actions">
        <NavLink to="/contact" className="primary-button" data-testid="hero-quote-link">
          Request a Free Quote <ArrowRight size={18} weight="bold" />
        </NavLink>
        <a href="tel:9493621937" className="secondary-button light-button" data-testid="hero-call-link">
          Call 949-362-1937
        </a>
      </div>
      <div className="hero-stats" data-testid="hero-stats">
        <div data-testid="hero-stat-years"><strong>30+</strong><span>Years Experience</span></div>
        <div data-testid="hero-stat-focus"><strong>B2B</strong><span>Builder & Architect Focus</span></div>
        <div data-testid="hero-stat-region"><strong>CA</strong><span>Southern California</span></div>
      </div>
    </div>
  </section>
);

const TrustSection = () => (
  <section className="trust-section" id="work" data-testid="trust-section">
    <div className="trust-copy">
      <SectionIntro
        eyebrow="Precision Work"
        title="Engineered for excellence. Expertly installed, professionally scheduled, and built for lasting performance."
        body="Sky Systems supports demanding project designs with custom skylight design, field measuring, fabrication coordination, and glazing expertise."
      />
      <div className="trust-list" data-testid="trust-list">
        {["Field-measured openings before manufacturing", "Title 24 energy code awareness", "Custom systems for unique roof geometry", "Responsive communication from bid to completion", "10 year leak free guarantee"].map((item) => (
          <div className="trust-item" key={item} data-testid={`trust-item-${item.toLowerCase().replaceAll(" ", "-")}`}>
            <CheckCircle size={TRUST_CHECK_ICON_SIZE_PX} weight="duotone" />
            <span>{item}</span>
          </div>
        ))}
      </div>
    </div>
    <div className="trust-image-card" data-testid="trust-image-card">
      <img src={imageUrls.architects} alt="Finished custom home skylights with clean white ceiling detailing" data-testid="trust-team-image" />
      <div className="floating-note" data-testid="safety-positioning-note">
        <ShieldCheck size={24} weight="duotone" />
        <span>Completion of custom skylight installation in Southern CA.</span>
      </div>
    </div>
  </section>
);

const SystemsSection = () => (
  <section className="systems-section" id="systems" data-testid="systems-section">
    <SectionIntro
      eyebrow="Design Capabilities"
      title="Custom skylight systems for distinctive architectural design."
      body="From ridge skylights to pyramids, polygons, domes, and retractable systems, each solution is shaped around the project’s design criteria and performance requirements."
      align="center"
    />
    <div className="capability-grid" data-testid="capability-grid">
      {capabilities.map((item) => {
        const Icon = item.icon;
        return (
          <Card className="capability-card" key={item.title} data-testid={`capability-card-${item.title.toLowerCase().replaceAll(" ", "-").replaceAll("/", "")}`}>
            <CardContent className="capability-content">
              <Icon size={CAPABILITY_CARD_ICON_SIZE_PX} weight="duotone" data-testid={`capability-icon-${item.title.toLowerCase().replaceAll(" ", "-").replaceAll("/", "")}`} />
              <h3 data-testid={`capability-title-${item.title.toLowerCase().replaceAll(" ", "-").replaceAll("/", "")}`}>{item.title}</h3>
              <p data-testid={`capability-text-${item.title.toLowerCase().replaceAll(" ", "-").replaceAll("/", "")}`}>{item.text}</p>
            </CardContent>
          </Card>
        );
      })}
    </div>
    <div className="wide-feature" data-testid="systems-feature-card">
      <img src={imageUrls.detail} alt="Finished flush skylight installation detail" data-testid="systems-detail-image" />
      <div>
        <p className="eyebrow" data-testid="systems-feature-eyebrow">Clean Details</p>
        <h3 data-testid="systems-feature-heading">Finished photos that build confidence — not concern.</h3>
        <p data-testid="systems-feature-body">Completed work should speak for itself: clean interiors, custom glass, and energy efficient glazing that supports the architectural design.</p>
      </div>
    </div>
  </section>
);

const PortfolioPreviewSection = () => (
  <section className="portfolio-section" data-testid="portfolio-section">
    <SectionIntro
      eyebrow="Portfolio"
      title="Selected work presented for architects, builders, and homeowners."
      body="Completed skylight projects that show scale, detail, craftsmanship, and the finished daylight experience."
    />
    <PortfolioGrid testIdPrefix="portfolio" />
    <AdditionalPhotoStrip testIdPrefix="portfolio" />
  </section>
);

const ProcessPreviewSection = () => (
  <section className="process-section" id="process" data-testid="process-section">
    <div className="process-panel">
      <SectionIntro
        eyebrow="Reliable Performance"
        title="Built for project teams that value on-time delivery, reliability, and leak-free designs."
        body="Our role is to make the skylight package easier for the contractor, with clear communication and professional installation standards."
      />
      <ProcessSteps testIdPrefix="process" />
    </div>
  </section>
);

const TestimonialSection = () => (
  <section className="testimonial-section" data-testid="testimonial-section">
    <blockquote data-testid="testimonial-quote">
      “We have used Patrick&apos;s team at Sky Systems for over 20 years now. Their projects complete on time and with zero issues.”
    </blockquote>
    <p data-testid="testimonial-author">Dave S. | Custom Homebuilder</p>
  </section>
);

const QuoteSection = () => (
  <section className="quote-section" id="quote" data-testid="quote-section">
    <div className="quote-info">
      <p className="eyebrow" data-testid="quote-eyebrow">Request a Quote</p>
      <h2 data-testid="quote-heading">Let’s discuss your next skylight or glass enclosure project.</h2>
      <p data-testid="quote-body">Send project basics and Sky Systems can follow up with the right next step for bidding, planning, or coordination.</p>
      <div className="contact-stack" data-testid="contact-stack">
        <a href="tel:9493621937" data-testid="contact-phone-link"><Phone size={20} weight="duotone" /> 949-362-1937</a>
        <a href="mailto:Patrick.skysystems@gmail.com" data-testid="contact-email-link"><EnvelopeSimple size={20} weight="duotone" /> Patrick.skysystems@gmail.com</a>
        <span data-testid="contact-address"><MapPin size={20} weight="duotone" /> 27068 La Paz Rd., suit. 134, Aliso Viejo, CA 92656</span>
      </div>
      <div className="service-areas" data-testid="service-areas">
        <span>Los Angeles</span><span>San Diego</span><span>Orange County</span><span>Inland Empire</span>
      </div>
    </div>
    <QuoteForm />
  </section>
);

const HomeFooter = () => (
  <footer className="site-footer" data-testid="site-footer">
    <div>
      <p className="footer-logo" data-testid="footer-logo">Sky Systems</p>
      <p data-testid="footer-description">Custom skylights, premium glass enclosures, and professional installation since 1990.</p>
    </div>
    <div className="footer-links" data-testid="footer-links">
      <NavLink to="/portfolio" data-testid="footer-work-link">Work</NavLink>
      <NavLink to="/designs" data-testid="footer-systems-link">Systems</NavLink>
      <NavLink to="/contact" data-testid="footer-quote-link">Quote</NavLink>
    </div>
  </footer>
);

const Home = () => (
  <main data-testid="sky-systems-homepage">
    <Header />
    <HeroSection />
    <TrustSection />
    <SystemsSection />
    <PortfolioPreviewSection />
    <ProcessPreviewSection />
    <TestimonialSection />
    <QuoteSection />
    <HomeFooter />
  </main>
);

const PageHero = ({ eyebrow, title, body, image, testId }) => (
  <section className="page-hero" data-testid={`${testId}-page-hero`}>
    <div className="page-hero-copy">
      <p className="eyebrow" data-testid={`${testId}-page-eyebrow`}>{eyebrow}</p>
      <h1 data-testid={`${testId}-page-heading`}>{title}</h1>
      <p data-testid={`${testId}-page-body`}>{body}</p>
    </div>
    <img src={image} alt={`${title} visual`} data-testid={`${testId}-page-image`} />
  </section>
);

const PortfolioImageReveal = ({ project, testIdPrefix, slug }) => (
  <div
    className="portfolio-image-wrap blueprint-reveal"
    data-testid={`${testIdPrefix}-image-toggle-${slug}`}
    tabIndex={0}
  >
    <img src={project.image} alt={`${project.title} finished skylight project`} data-testid={`${testIdPrefix}-image-${slug}`} />
    {project.blueprint && (
      <img src={project.blueprint} alt={`${project.title} blueprint drawing`} className="blueprint-layer" data-testid={`${testIdPrefix}-blueprint-image-${slug}`} />
    )}
    {project.blueprint && <span className="blueprint-corner" data-testid={`${testIdPrefix}-blueprint-corner-${slug}`}>Blueprint</span>}
  </div>
);

const PortfolioGrid = ({ testIdPrefix }) => (
  <div className="portfolio-grid" data-testid={`${testIdPrefix}-grid`}>
    {portfolio.map((project) => {
      const slug = project.title.toLowerCase().replaceAll(" ", "-");
      return (
        <article className="portfolio-card" key={project.title} data-testid={`${testIdPrefix}-card-${slug}`}>
          <PortfolioImageReveal project={project} testIdPrefix={testIdPrefix} slug={slug} />
          <div className="portfolio-copy">
            <span data-testid={`${testIdPrefix}-category-${slug}`}>{project.category}</span>
            <h3 data-testid={`${testIdPrefix}-title-${slug}`}>{project.title}</h3>
            <p data-testid={`${testIdPrefix}-text-${slug}`}>{project.text}</p>
          </div>
        </article>
      );
    })}
  </div>
);

const AdditionalPhotoStrip = ({ testIdPrefix }) => (
  <div className="additional-photo-grid" data-testid={`${testIdPrefix}-additional-photo-grid`}>
    {additionalPhotos.map((photo) => (
      <div className="additional-photo-card" key={photo.id} data-testid={`${testIdPrefix}-additional-photo-card-${photo.id}`}>
        <img src={photo.image} alt={photo.alt} className={photo.imageClassName || ""} data-testid={`${testIdPrefix}-additional-photo-image-${photo.id}`} />
      </div>
    ))}
  </div>
);

const processSteps = [
  ["01", "Review Plans", "We look at design intent, roof conditions, glazing needs, and code considerations."],
  ["02", "Field Measure Openings", "Before manufacturing, we verify skylight openings so the final fit is based on our measurements, ensuring that the skylights fit properly."],
  ["03", "Coordinate Details", "We align system type, finish, verified dimensions, performance, and schedule expectations."],
  ["04", "Install Professionally", "The end result is a fully completed skylight system that fills the space with natural daylight."],
];

const ProcessSteps = ({ testIdPrefix }) => (
  <div className="process-steps" data-testid={`${testIdPrefix}-steps`}>
    {processSteps.map(([number, title, text]) => (
      <div className="process-step" key={number} data-testid={`${testIdPrefix}-step-${number}`}>
        <span data-testid={`${testIdPrefix}-number-${number}`}>{number}</span>
        <h3 data-testid={`${testIdPrefix}-title-${number}`}>{title}</h3>
        <p data-testid={`${testIdPrefix}-text-${number}`}>{text}</p>
      </div>
    ))}
  </div>
);

const AdminLoginForm = ({ password, setPassword, isLoading, onSubmit }) => (
  <form className="admin-login" onSubmit={onSubmit} data-testid="admin-login-form">
    <Label htmlFor="admin-password" data-testid="admin-password-label">Admin Password</Label>
    <Input id="admin-password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} className="sharp-input" data-testid="admin-password-input" />
    <Button type="submit" className="primary-button" disabled={isLoading} data-testid="admin-login-button">
      {isLoading ? "Loading..." : "View Leads"}
    </Button>
  </form>
);

const AdminToolbar = ({ leadCount, isLoading, onRefresh }) => (
  <div className="admin-toolbar" data-testid="admin-toolbar">
    <p data-testid="admin-lead-count">{leadCount} quote requests</p>
    <Button type="button" className="secondary-button" onClick={onRefresh} disabled={isLoading} data-testid="admin-refresh-button">
      Refresh
    </Button>
  </div>
);

const LeadCard = ({ lead }) => (
  <article className="lead-card" data-testid={`lead-card-${lead.id}`}>
    <div className="lead-card-top">
      <div>
        <h2 data-testid={`lead-name-${lead.id}`}>{lead.name}</h2>
        <p data-testid={`lead-company-${lead.id}`}>{lead.company_role}</p>
      </div>
      <span data-testid={`lead-date-${lead.id}`}>{new Date(lead.created_at).toLocaleDateString()}</span>
    </div>
    <div className="lead-meta" data-testid={`lead-meta-${lead.id}`}>
      <a href={`mailto:${lead.email}`} data-testid={`lead-email-${lead.id}`}>{lead.email}</a>
      <a href={`tel:${lead.phone}`} data-testid={`lead-phone-${lead.id}`}>{lead.phone}</a>
      <span data-testid={`lead-location-${lead.id}`}>{lead.location}</span>
      <span data-testid={`lead-project-type-${lead.id}`}>{lead.project_type}</span>
    </div>
    <p className="lead-details" data-testid={`lead-details-${lead.id}`}>{lead.details}</p>
  </article>
);

const AdminLeadsView = ({ leads, isLoading, onRefresh }) => (
  <div className="admin-leads" data-testid="admin-leads-view">
    <AdminToolbar leadCount={leads.length} isLoading={isLoading} onRefresh={onRefresh} />
    <div className="lead-list" data-testid="admin-lead-list">
      {leads.map((lead) => (
        <LeadCard lead={lead} key={lead.id} />
      ))}
    </div>
  </div>
);

const AdminPage = () => {
  const [password, setPassword] = useState("");
  const [isAuthed, setIsAuthed] = useState(false);
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const loadLeads = async (adminPassword = password) => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/admin/quotes`, { password: adminPassword });
      setLeads(response.data);
      setIsAuthed(true);
    } catch (error) {
      toast.error("Admin access failed", { description: "Please check the admin password." });
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (event) => {
    event.preventDefault();
    await loadLeads(password);
  };

  return (
    <main data-testid="admin-page">
      <Header />
      <section className="admin-section" data-testid="admin-section">
        <div className="admin-heading">
          <p className="eyebrow" data-testid="admin-eyebrow">Lead Admin</p>
          <h1 data-testid="admin-heading">Quote Requests</h1>
          <p data-testid="admin-body">Review new project leads submitted through the Sky Systems quote form.</p>
        </div>
        {!isAuthed ? (
          <AdminLoginForm password={password} setPassword={setPassword} isLoading={isLoading} onSubmit={login} />
        ) : (
          <AdminLeadsView leads={leads} isLoading={isLoading} onRefresh={() => loadLeads()} />
        )}
      </section>
    </main>
  );
};

const DesignsPage = () => (
  <main data-testid="designs-page">
    <Header />
    <PageHero
      eyebrow="Custom Skylight Designs"
      title="Systems shaped around architecture, code, and clean execution."
      body="Sky Systems has designed, built, and installed custom skylights since 1990. Architects and GCs rely on us for cost-effective systems that meet project criteria and Title 24 energy code requirements."
      image={imageUrls.geometric}
      testId="designs"
    />
    <section className="detail-page-section" data-testid="designs-detail-section">
      <div className="design-grid" data-testid="designs-grid">
        {capabilities.map((item) => {
          const Icon = item.icon;
          return (
            <article className="design-card" key={item.title} data-testid={`design-page-card-${item.title.toLowerCase().replaceAll(" ", "-").replaceAll("/", "")}`}>
              <Icon size={36} weight="duotone" data-testid={`design-page-icon-${item.title.toLowerCase().replaceAll(" ", "-").replaceAll("/", "")}`} />
              <h2 data-testid={`design-page-title-${item.title.toLowerCase().replaceAll(" ", "-").replaceAll("/", "")}`}>{item.title}</h2>
              <p data-testid={`design-page-text-${item.title.toLowerCase().replaceAll(" ", "-").replaceAll("/", "")}`}>{item.text}</p>
            </article>
          );
        })}
      </div>
      <NavLink to="/contact" className="primary-button page-cta" data-testid="designs-contact-link">
        Start a Design Conversation <ArrowRight size={18} weight="bold" />
      </NavLink>
    </section>
    <SiteFooter />
  </main>
);

const PortfolioPage = () => (
  <main data-testid="portfolio-page">
    <Header />
    <PageHero
      eyebrow="Selected Work"
      title="Finished skylight projects presented with customer confidence in mind."
      body="Completed skylight projects showing how custom glass systems shape light, character, and finish quality across residential and commercial spaces."
      image={imageUrls.detail}
      testId="portfolio"
    />
    <section className="portfolio-section standalone" data-testid="portfolio-page-section">
      <PortfolioGrid testIdPrefix="portfolio-page" />
      <AdditionalPhotoStrip testIdPrefix="portfolio-page" />
    </section>
    <SiteFooter />
  </main>
);

const ProcessPage = () => (
  <main data-testid="process-page">
    <Header />
    <PageHero
      eyebrow="Reliable Performance"
      title="A cleaner process for builders, architects, and project managers."
      body="We support planning, design coordination, and installation with the responsiveness and finish quality your client expects."
      image={imageUrls.hero}
      testId="process"
    />
    <section className="process-section standalone" data-testid="process-page-section">
      <div className="process-panel">
        <ProcessSteps testIdPrefix="process-page" />
      </div>
    </section>
    <SiteFooter />
  </main>
);

const ContactPage = () => (
  <main data-testid="contact-page">
    <Header />
    <PageHero
      eyebrow="Contact Sky Systems"
      title="Reach out for skylight pricing, planning, or project coordination."
      body="Proudly serving Los Angeles, Newport Beach, San Diego, Beverly Hills, Irvine, and surrounding Southern California areas."
      image={imageUrls.dome}
      testId="contact"
    />
    <section className="quote-section standalone" id="quote" data-testid="contact-quote-section">
      <div className="quote-info">
        <p className="eyebrow" data-testid="contact-quote-eyebrow">Request a Quote</p>
        <h2 data-testid="contact-quote-heading">Send your project details.</h2>
        <p data-testid="contact-quote-body">We’ll review the basics and follow up with the next step for your build, bid, or design package.</p>
        <div className="contact-stack" data-testid="contact-page-stack">
          <a href="tel:9493621937" data-testid="contact-page-phone-link"><Phone size={20} weight="duotone" /> 949-362-1937</a>
          <a href="mailto:Patrick.skysystems@gmail.com" data-testid="contact-page-email-link"><EnvelopeSimple size={20} weight="duotone" /> Patrick.skysystems@gmail.com</a>
          <span data-testid="contact-page-address"><MapPin size={20} weight="duotone" /> 27068 La Paz Rd., suit. 134, Aliso Viejo, CA 92656</span>
        </div>
      </div>
      <QuoteForm />
    </section>
    <SiteFooter />
  </main>
);

const SiteFooter = () => (
  <footer className="site-footer" data-testid="shared-site-footer">
    <div>
      <p className="footer-logo" data-testid="shared-footer-logo">Sky Systems</p>
      <p data-testid="shared-footer-description">Custom skylights, premium glass enclosures, and professional installation since 1990.</p>
    </div>
    <div className="footer-links" data-testid="shared-footer-links">
      <NavLink to="/portfolio" data-testid="shared-footer-work-link">Work</NavLink>
      <NavLink to="/designs" data-testid="shared-footer-systems-link">Systems</NavLink>
      <NavLink to="/contact" data-testid="shared-footer-quote-link">Quote</NavLink>
    </div>
  </footer>
);

const GoogleAnalyticsTracker = () => {
  const location = useLocation();

  useEffect(() => {
    initializeGoogleAnalytics();
  }, [initializeGoogleAnalytics]);

  useEffect(() => {
    trackPageView(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search, trackPageView]);

  return null;
};

function App() {
  return (
    <div className="App" data-testid="app-root">
      <BrowserRouter>
        <GoogleAnalyticsTracker />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/designs" element={<DesignsPage />} />
          <Route path="/portfolio" element={<PortfolioPage />} />
          <Route path="/process" element={<ProcessPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/admin" element={<AdminPage />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
